/**
 * AnalyticsPage — Relatórios & Analytics
 *
 * Seções:
 *  1. KPI row (4 cartões rápidos)
 *  2. Crescimento de contatos (line chart SVG, 6 meses)
 *  3. Distribuição por categoria (donut SVG)
 *  4. Top bairros (barras horizontais CSS)
 *  5. Performance de campanhas (barras verticais SVG)
 */

import { useContactStats } from '@/features/crm/hooks/useContacts';
import {
  useContactGrowth,
  useTopNeighborhoods,
  useCampaignPerformance,
  useAnalyticsKpis,
} from '../hooks/useAnalytics';
import { useVoiceInsightsSummary } from '@/features/crm/hooks/useVoiceNotes';
import {
  Users, MessageSquare, Send, Calendar,
  TrendingUp, MapPin, BarChart2, Loader2,
  Mic, Smile, Meh, Frown, MinusCircle, Tags, Sparkles,
} from 'lucide-react';

/* ── Paleta de categorias ────────────────────────────────────────────────────── */
const CAT_CONFIG: Record<string, { label: string; color: string; stroke: string }> = {
  lideranca:    { label: 'Liderança',    color: 'bg-emerald-400',  stroke: '#34d399' },
  voluntario:   { label: 'Voluntário',   color: 'bg-blue-400',     stroke: '#60a5fa' },
  doador:       { label: 'Doador',       color: 'bg-amber-400',    stroke: '#fbbf24' },
  eleitor:      { label: 'Eleitor',      color: 'bg-fuchsia-400',  stroke: '#e879f9' },
  simpatizante: { label: 'Simpatizante', color: 'bg-cyan-400',     stroke: '#22d3ee' },
};

/* ─────────────────────────── Componentes de gráfico ──────────────────────────*/

/* ── Line chart (contato crescimento) ──────────────────────────────────────── */

interface LinePoint { label: string; value: number; total: number }

function LineChart({ data }: { data: LinePoint[] }) {
  if (data.length < 2) {
    return (
      <div className="flex items-center justify-center h-40 text-sm text-ink-subtle">
        Dados insuficientes.
      </div>
    );
  }

  const W = 600; const H = 160; const PX = 10; const PY = 12;
  const maxVal = Math.max(...data.map((d) => d.value), 1);

  const pts = data.map((d, i) => ({
    x: PX + (i / (data.length - 1)) * (W - 2 * PX),
    y: PY + (1 - d.value / maxVal) * (H - 2 * PY),
    d,
  }));

  /* Smooth bezier path */
  const linePath = pts.reduce((acc, p, i) => {
    if (i === 0) return `M ${p.x},${p.y}`;
    const prev = pts[i - 1];
    const cx = (prev.x + p.x) / 2;
    return acc + ` C ${cx},${prev.y} ${cx},${p.y} ${p.x},${p.y}`;
  }, '');

  const areaPath =
    `M ${pts[0].x},${H - PY} ` +
    pts.map((p) => `L ${p.x},${p.y}`).join(' ') +
    ` L ${pts[pts.length - 1].x},${H - PY} Z`;

  return (
    <div className="relative w-full">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full overflow-visible">
        <defs>
          <linearGradient id="lg-growth" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="var(--color-accent)" stopOpacity="0.25" />
            <stop offset="100%" stopColor="var(--color-accent)" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Área preenchida */}
        <path d={areaPath} fill="url(#lg-growth)" />

        {/* Linha */}
        <path d={linePath} fill="none" stroke="var(--color-accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

        {/* Pontos + tooltip simples via title */}
        {pts.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r={4} fill="var(--color-midnight-3)" stroke="var(--color-accent)" strokeWidth="2" />
            <title>{p.d.label}: {p.d.value} novos (total {p.d.total.toLocaleString('pt-BR')})</title>
          </g>
        ))}
      </svg>

      {/* Labels do eixo X */}
      <div className="flex justify-between mt-1 px-2">
        {data.map((d) => (
          <span key={d.label} className="text-[10px] font-mono text-ink-subtle capitalize w-8 text-center">
            {d.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── Donut chart (categorias) ───────────────────────────────────────────────── */

interface DonutSlice { key: string; value: number; stroke: string }

function DonutChart({ slices, total }: { slices: DonutSlice[]; total: number }) {
  const R   = 52;
  const circ = 2 * Math.PI * R;
  const cx = 68; const cy = 68;

  let offset = 0;
  const nonZero = slices.filter((s) => s.value > 0);

  return (
    <svg width={136} height={136} viewBox="0 0 136 136">
      {/* Trilha de fundo */}
      <circle cx={cx} cy={cy} r={R} fill="none" stroke="var(--color-midnight-4)" strokeWidth={14} />

      {nonZero.length === 0 ? (
        <circle cx={cx} cy={cy} r={R} fill="none" stroke="var(--color-midnight-4)" strokeWidth={14} />
      ) : (
        nonZero.map((s) => {
          const arc  = (s.value / total) * circ;
          const el = (
            <circle
              key={s.key}
              cx={cx} cy={cy} r={R}
              fill="none"
              stroke={s.stroke}
              strokeWidth={14}
              strokeDasharray={`${arc} ${circ}`}
              strokeDashoffset={-(offset)}
              style={{ transform: 'rotate(-90deg)', transformOrigin: `${cx}px ${cy}px` }}
            />
          );
          offset += arc;
          return el;
        })
      )}

      {/* Centro */}
      <text x={cx} y={cy - 4} textAnchor="middle" className="font-display" fill="var(--color-ink)" fontSize={20} fontWeight={800}>
        {total.toLocaleString('pt-BR')}
      </text>
      <text x={cx} y={cy + 13} textAnchor="middle" fill="var(--color-ink-subtle)" fontSize={9} fontWeight={600}>
        ATIVOS
      </text>
    </svg>
  );
}

/* ── Barra horizontal ────────────────────────────────────────────────────────── */

function HBar({ label, value, max, color = 'bg-accent' }: { label: string; value: number; max: number; color?: string }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="w-28 shrink-0 text-xs text-ink-muted truncate text-right">{label}</span>
      <div className="flex-1 h-2 rounded-full bg-midnight-4 overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all duration-700`} style={{ width: `${pct}%` }} />
      </div>
      <span className="w-10 shrink-0 text-right font-mono text-xs text-ink-muted">{value}</span>
    </div>
  );
}

/* ── Barras de campanha ──────────────────────────────────────────────────────── */

interface CampBar { id?: string; name: string; sent: number; delivered: number; read: number; date: string }

function CampaignBars({ data }: { data: CampBar[] }) {
  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-2 text-center">
        <Send className="h-8 w-8 text-ink-faint" />
        <p className="text-sm text-ink-muted">Nenhuma campanha disparada ainda.</p>
      </div>
    );
  }

  const maxVal = Math.max(...data.map((d) => d.sent), 1);
  const H      = 120; const BAR_W = 16; const GAP = 8;

  return (
    <div className="w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${data.length * (3 * BAR_W + GAP * 4)} ${H + 24}`}
        className="w-full min-w-[280px]"
        preserveAspectRatio="xMidYMid meet"
      >
        {data.map((d, i) => {
          const groupX = i * (3 * BAR_W + GAP * 4) + GAP;
          const sentH  = (d.sent      / maxVal) * H;
          const delivH = (d.delivered / maxVal) * H;
          const readH  = (d.read      / maxVal) * H;
          return (
            <g key={d.id ?? i}>
              {/* Sent */}
              <rect x={groupX}             y={H - sentH}  width={BAR_W} height={sentH}  rx={3} fill="var(--color-accent)" opacity={0.35}>
                <title>Enviadas: {d.sent.toLocaleString('pt-BR')}</title>
              </rect>
              {/* Delivered */}
              <rect x={groupX + BAR_W + 2} y={H - delivH} width={BAR_W} height={delivH} rx={3} fill="var(--color-accent)" opacity={0.7}>
                <title>Entregues: {d.delivered.toLocaleString('pt-BR')}</title>
              </rect>
              {/* Read */}
              <rect x={groupX + 2*(BAR_W + 2)} y={H - readH}  width={BAR_W} height={readH}  rx={3} fill="var(--color-accent)">
                <title>Lidas: {d.read.toLocaleString('pt-BR')}</title>
              </rect>
              {/* Date label */}
              <text x={groupX + BAR_W + 2} y={H + 14} textAnchor="middle" fill="var(--color-ink-subtle)" fontSize={8}>
                {d.date}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Legenda */}
      <div className="flex items-center gap-4 mt-2">
        {[
          { label: 'Enviadas',  opacity: 'opacity-35' },
          { label: 'Entregues', opacity: 'opacity-70' },
          { label: 'Lidas',     opacity: 'opacity-100' },
        ].map(({ label, opacity }) => (
          <div key={label} className="flex items-center gap-1.5">
            <div className={`h-2 w-4 rounded-sm bg-accent ${opacity}`} />
            <span className="text-[10px] text-ink-subtle">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Skeleton ────────────────────────────────────────────────────────────────── */

function ChartSkeleton({ lines = 4 }: { lines?: number }) {
  return (
    <div className="space-y-3 animate-pulse">
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="h-6 rounded bg-midnight-4" style={{ opacity: 1 - i * 0.15, width: `${85 - i * 8}%` }} />
      ))}
    </div>
  );
}

/* ── Card wrapper ─────────────────────────────────────────────────────────────── */

function ChartCard({ title, icon: Icon, iconColor = 'text-accent', children, className = '' }: {
  title: string; icon: React.ElementType; iconColor?: string;
  children: React.ReactNode; className?: string;
}) {
  return (
    <div className={`rounded-2xl card-premium p-6 space-y-4 ${className}`}>
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${iconColor}`} />
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] font-semibold text-ink-muted">{title}</span>
      </div>
      {children}
    </div>
  );
}

/* ── KPI Card ────────────────────────────────────────────────────────────────── */

function KpiCard({ title, value, sub, icon: Icon, accent, iconColor }: {
  title: string; value: string | number; sub: string;
  icon: React.ElementType; accent: string; iconColor: string;
}) {
  return (
    <div className={`relative overflow-hidden rounded-2xl card-premium p-5`}>
      <div className={`pointer-events-none absolute -top-10 -right-10 h-28 w-28 rounded-full bg-gradient-to-br ${accent} blur-2xl opacity-50`} />
      <div className="relative flex items-start justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] font-semibold text-ink-muted">{title}</span>
        <span className={`rounded-xl bg-midnight-2 hairline p-2 ${iconColor}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="relative mt-4">
        <p className="font-display text-3xl font-extrabold tracking-tight text-white">
          {typeof value === 'number' ? value.toLocaleString('pt-BR') : value}
        </p>
        <p className="text-[10px] text-ink-subtle mt-1">{sub}</p>
      </div>
    </div>
  );
}

/* ─────────────────────────── Página principal ────────────────────────────── */

export function AnalyticsPage() {
  const { data: stats,    isLoading: statsLoading   } = useContactStats();
  const { data: growth,   isLoading: growthLoading  } = useContactGrowth();
  const { data: hoods,    isLoading: hoodsLoading   } = useTopNeighborhoods();
  const { data: campPerf, isLoading: campLoading    } = useCampaignPerformance();
  const { data: kpis,     isLoading: kpisLoading    } = useAnalyticsKpis();
  const { data: voiceInsights, isLoading: voiceLoading } = useVoiceInsightsSummary(30);

  /* Donut slices */
  const donutTotal  = stats?.total ?? 0;
  const donutSlices = Object.entries(CAT_CONFIG).map(([key, cfg]) => ({
    key,
    value:  stats?.[key as keyof typeof stats] as number ?? 0,
    stroke: cfg.stroke,
  }));

  const maxHood = hoods?.[0]?.count ?? 1;

  return (
    <div className="space-y-8 pb-12 animate-fade-in">

      {/* ── Header ── */}
      <div>
        <h1 className="font-display text-2xl font-bold text-ink">Relatórios</h1>
        <p className="text-sm text-ink-muted mt-1">
          Visão consolidada da base, engajamento e performance de campanhas.
        </p>
      </div>

      {/* ── KPIs ── */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpisLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-2xl card-premium p-5 animate-pulse h-28 bg-midnight-4/30" />
          ))
        ) : (
          <>
            <KpiCard
              title="Campanhas no mês"
              value={kpis?.campaignsThisMonth ?? 0}
              sub="WhatsApp disparadas"
              icon={Send}
              accent="from-fuchsia-500/30 to-pink-500/10"
              iconColor="text-fuchsia-300"
            />
            <KpiCard
              title="Mensagens enviadas"
              value={kpis?.messagesThisMonth ?? 0}
              sub="no mês corrente"
              icon={MessageSquare}
              accent="from-blue-500/30 to-indigo-500/10"
              iconColor="text-blue-300"
            />
            <KpiCard
              title="Taxa de entrega"
              value={kpis?.avgDeliveryRate ? `${kpis.avgDeliveryRate}%` : '—'}
              sub="entregues / enviadas"
              icon={TrendingUp}
              accent="from-emerald-500/30 to-teal-500/10"
              iconColor="text-emerald-300"
            />
            <KpiCard
              title="Eventos no mês"
              value={kpis?.eventsThisMonth ?? 0}
              sub="criados este mês"
              icon={Calendar}
              accent="from-amber-500/30 to-orange-500/10"
              iconColor="text-amber-300"
            />
          </>
        )}
      </section>

      {/* ── Crescimento + Categorias ── */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        <ChartCard title="Crescimento de Contatos" icon={TrendingUp} className="lg:col-span-2">
          <div className="space-y-1">
            {growthLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="h-6 w-6 animate-spin text-accent" />
              </div>
            ) : (
              <>
                <LineChart data={growth ?? []} />
                <p className="text-[10px] text-ink-subtle text-right">Novos contatos por mês (últimos 6 meses)</p>
              </>
            )}
          </div>
        </ChartCard>

        <ChartCard title="Por Categoria" icon={Users}>
          {statsLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-6 w-6 animate-spin text-accent" />
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4">
              <DonutChart slices={donutSlices} total={donutTotal} />
              <div className="w-full space-y-1.5">
                {Object.entries(CAT_CONFIG).map(([key, cfg]) => {
                  const val = stats?.[key as keyof typeof stats] as number ?? 0;
                  if (val === 0) return null;
                  const pct = donutTotal > 0 ? Math.round((val / donutTotal) * 100) : 0;
                  return (
                    <div key={key} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <div className={`h-2 w-2 rounded-full ${cfg.color}`} />
                        <span className="text-ink-muted">{cfg.label}</span>
                      </div>
                      <span className="font-mono text-ink-subtle">{val.toLocaleString('pt-BR')} ({pct}%)</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </ChartCard>
      </section>

      {/* ── Bairros + Campanhas ── */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        <ChartCard title="Top Bairros" icon={MapPin} iconColor="text-amber-300">
          {hoodsLoading ? (
            <ChartSkeleton lines={8} />
          ) : !hoods || hoods.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <MapPin className="h-8 w-8 text-ink-faint" />
              <p className="text-sm text-ink-muted">Nenhum bairro cadastrado ainda.</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {hoods.map((h) => (
                <HBar
                  key={h.neighborhood}
                  label={h.neighborhood}
                  value={h.count}
                  max={maxHood}
                  color="bg-amber-400"
                />
              ))}
            </div>
          )}
        </ChartCard>

        <ChartCard title="Performance de Campanhas" icon={BarChart2} iconColor="text-fuchsia-300">
          {campLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-6 w-6 animate-spin text-accent" />
            </div>
          ) : (
            <CampaignBars data={campPerf ?? []} />
          )}
        </ChartCard>
      </section>

      {/* ── Inteligência de campo (Voice Notes NLU) ── */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <Mic className="h-4 w-4 text-accent" />
          <h2 className="font-display text-lg font-bold text-ink">Inteligência de campo</h2>
          <span className="text-[10px] text-ink-subtle ml-1">últimos 30 dias</span>
        </div>

        <FieldIntelligenceSection
          insights={voiceInsights}
          isLoading={voiceLoading}
        />
      </section>
    </div>
  );
}

// ── Seção de inteligência de campo ───────────────────────────────────────────

const SENTIMENT_META = {
  positive: { label: 'Positivo', icon: Smile,       color: 'text-emerald-300', stroke: '#34d399' },
  neutral:  { label: 'Neutro',   icon: Meh,         color: 'text-ink-muted',   stroke: '#94a3b8' },
  negative: { label: 'Negativo', icon: Frown,       color: 'text-danger',      stroke: '#ef4444' },
  mixed:    { label: 'Misto',    icon: MinusCircle, color: 'text-amber-300',   stroke: '#fbbf24' },
} as const;

function FieldIntelligenceSection({
  insights,
  isLoading,
}: {
  insights: ReturnType<typeof useVoiceInsightsSummary>['data'];
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded-2xl card-premium p-5 animate-pulse h-48 bg-midnight-4/30" />
        ))}
      </div>
    );
  }

  if (!insights || insights.total_notes === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-hairline p-8 text-center">
        <Mic className="h-8 w-8 text-ink-subtle mx-auto mb-3" />
        <p className="text-sm text-ink-muted">Nenhuma nota de voz analisada nos últimos 30 dias.</p>
        <p className="text-[11px] text-ink-faint mt-1">
          Use o gravador no detalhe de qualquer contato para começar.
        </p>
      </div>
    );
  }

  const sentimentTotal = (Object.values(insights.sentiment_dist) as number[])
    .reduce((acc, v) => acc + (v ?? 0), 0);

  const sentimentSlices = (Object.entries(insights.sentiment_dist) as Array<[keyof typeof SENTIMENT_META, number]>)
    .filter(([, v]) => (v ?? 0) > 0)
    .map(([key, value]) => ({
      key,
      value,
      stroke: SENTIMENT_META[key]?.stroke ?? '#94a3b8',
    }));

  const maxTheme = insights.top_themes[0]?.count ?? 1;

  // Sentimento dominante (label do KPI)
  const dominant = sentimentSlices
    .slice()
    .sort((a, b) => b.value - a.value)[0];
  const dominantMeta = dominant ? SENTIMENT_META[dominant.key] : null;
  const DominantIcon = dominantMeta?.icon ?? Meh;

  return (
    <>
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          title="Notas captadas"
          value={insights.total_notes}
          sub="últimos 30 dias"
          icon={Mic}
          accent="from-accent/30 to-accent/10"
          iconColor="text-accent"
        />
        <KpiCard
          title="Engajamento médio"
          value={insights.avg_engagement !== null ? `${insights.avg_engagement}/100` : '—'}
          sub="0-30 frio · 31-70 morno · 71-100 aliado"
          icon={TrendingUp}
          accent="from-emerald-500/30 to-teal-500/10"
          iconColor="text-emerald-300"
        />
        <KpiCard
          title="Sentimento dominante"
          value={dominantMeta?.label ?? '—'}
          sub={dominant ? `${dominant.value} de ${sentimentTotal}` : 'sem dados'}
          icon={DominantIcon}
          accent="from-fuchsia-500/30 to-pink-500/10"
          iconColor={dominantMeta?.color ?? 'text-ink-muted'}
        />
        <KpiCard
          title="Notas georreferenciadas"
          value={insights.geo_notes}
          sub="com lat/lng capturado"
          icon={MapPin}
          accent="from-amber-500/30 to-orange-500/10"
          iconColor="text-amber-300"
        />
      </div>

      {/* Donut sentimento + Top temas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Distribuição de sentimento" icon={Sparkles}>
          {sentimentSlices.length === 0 ? (
            <p className="text-sm text-ink-muted text-center py-8">Sem dados de sentimento ainda.</p>
          ) : (
            <div className="flex flex-col items-center gap-4">
              <DonutChart slices={sentimentSlices} total={sentimentTotal} />
              <div className="w-full space-y-1.5">
                {sentimentSlices.map(({ key, value }) => {
                  const meta = SENTIMENT_META[key];
                  const pct  = sentimentTotal > 0 ? Math.round((value / sentimentTotal) * 100) : 0;
                  return (
                    <div key={key} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: meta.stroke }} />
                        <span className="text-ink-muted">{meta.label}</span>
                      </div>
                      <span className="font-mono text-ink-subtle">{value} ({pct}%)</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </ChartCard>

        <ChartCard title="Top temas mencionados" icon={Tags} iconColor="text-blue-300">
          {insights.top_themes.length === 0 ? (
            <p className="text-sm text-ink-muted text-center py-8">Sem temas detectados ainda.</p>
          ) : (
            <div className="space-y-2.5">
              {insights.top_themes.map((t) => (
                <HBar
                  key={t.theme}
                  label={t.theme}
                  value={t.count}
                  max={maxTheme}
                  color="bg-blue-400"
                />
              ))}
            </div>
          )}
        </ChartCard>
      </div>
    </>
  );
}
